import { Canvas, FabricImage, Textbox } from 'fabric';
import { useEffect, useRef, useState, useCallback } from 'react';
import mass from './mass.png';
import cystic from './cystic.png';
import hypoechoic from './hypoechoic.png';
import lymph from './lymph.png';
import mixed from './mixed.png';
import nonmass from './nonmass.png';
import solid from './solid.png';
import arrow from './arrow.png';
import { Button, Checkbox, Col, Form, Input, Row, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { blobToBase64 } from '@vindr/utils/converter';
import FontSizeOutlined from '@ant-design/icons/FontSizeOutlined';

interface ImageProps {
  src: string;
  width: number;
  height: number;
  manualAdjustment: boolean;
  ratio: number; // width / height
}

const MedicalImageEdit = ({
  imageProps,
  onSaveACopy,
  onSave,
  onCancel,
}: {
  imageProps: ImageProps;
  onSaveACopy: (image, imageProps: ImageProps) => void;
  onSave: (image, imageProps: ImageProps) => void;
  onCancel: () => void;
}) => {
  const [imageSize, setImageSize] = useState({
    width: imageProps?.width,
    height: imageProps?.height,
    manualAdjustment: imageProps?.manualAdjustment,
    ratio: imageProps?.ratio,
  });
  const { t } = useTranslation('Vindoc');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState(null);
  const [isRightMouseDown, setIsRightMouseDown] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [imageObject, setImageObject] = useState<FabricImage | null>(null);
  const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const annotations = [
    { name: 'Cystic', src: cystic },
    { name: 'Solid', src: solid },
    { name: 'Mixed cystic and solid', src: mixed },
    { name: 'Hypoechoic region', src: hypoechoic },
    { name: 'Intraductal mass', src: mass },
    { name: 'Nonmass enhancement', src: nonmass },
    { name: 'Lymph node', src: lymph },
    { name: 'Arrow', src: arrow },
  ];

  useEffect(() => {
    setImageSize({
      width: imageProps?.width,
      height: imageProps?.height,
      manualAdjustment: imageProps?.manualAdjustment,
      ratio: imageProps?.ratio,
    });
  }, [imageProps?.width, imageProps?.height, imageProps?.manualAdjustment, imageProps?.ratio]);

  useEffect(() => {
    if (!canvas || !imageObject) return;
    imageObject?.set({
      scaleX: imageSize?.width / imageObject?.width,
      scaleY: imageSize?.height / imageObject?.height,
    });
    canvas?.renderAll();
  }, [imageSize?.width, imageSize?.height, imageObject, canvas]);

  const handleChangeAdjustment = (value: boolean) => {
    setImageSize((prev) => ({ ...prev, manualAdjustment: value }));
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageSize((prev) => ({
      ...prev,
      width: +e.target.value,
      ...(!imageSize.manualAdjustment ? { height: +e.target.value / imageSize.ratio } : {}),
    }));
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageSize((prev) => ({
      ...prev,
      height: +e.target.value,
      ...(!imageSize.manualAdjustment ? { width: +e.target.value * imageSize.ratio } : {}),
    }));
  };

  const loadImage = useCallback(
    async (src) => {
      const imageBlob = await (await fetch(src)).blob();
      const imageSrc = await blobToBase64(imageBlob);
      const img = await FabricImage.fromURL(imageSrc);
      img.set({
        selectable: false,
        evented: false,
      });
      setImageObject(img);
      canvas?.add(img);
      canvas?.sendObjectToBack(img);
    },
    [canvas]
  );

  useEffect(() => {
    if (imageProps?.src && canvas) {
      loadImage(imageProps.src);
    }
  }, [imageProps, canvas, loadImage]);

  useEffect(() => {
    const fabricCanvas = new Canvas(canvasRef.current, {
      width: 500,
      height: 500,
    });
    setCanvas(fabricCanvas);

    const preventContextMenu = (e: MouseEvent) => e.preventDefault();
    if (fabricCanvas.upperCanvasEl) {
      fabricCanvas.upperCanvasEl.addEventListener('contextmenu', preventContextMenu);
    }

    return () => {
      if (fabricCanvas.upperCanvasEl) {
        fabricCanvas.upperCanvasEl.removeEventListener('contextmenu', preventContextMenu);
      }
      fabricCanvas.dispose();
    };
  }, []);

  const onAddImage = async (src) => {
    const img = await FabricImage.fromURL(src);
    const zoom = canvas.getZoom();
    const transform = canvas.viewportTransform;
    const panX = transform[4];
    const panY = transform[5];
    img.set({
      left: (0 - panX) / zoom,
      top: (0 - panY) / zoom,
      selectable: true,
    });
    canvas?.add(img);
    canvas?.setActiveObject(img);
  };
  const onAddText = async (text: string) => {
    if (!canvas) return;
    const zoom = canvas.getZoom();
    const transform = canvas.viewportTransform;
    const panX = transform[4];
    const panY = transform[5];
    const textBox = new Textbox(text, {
      left: (0 - panX) / zoom,
      top: (0 - panY) / zoom,
      fontSize: 16,
      fill: 'black',
      width: 100,
      // backgroundColor: "yellow",
      padding: 5,
      editable: true,
    });

    canvas.add(textBox);
    canvas.setActiveObject(textBox);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.button === 2) {
      setIsRightMouseDown(true);
    } else if (event.button === 1) {
      setIsPanning(true);
      lastPosRef.current = { x: event.clientX, y: event.clientY };
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!canvas) return;

    if (isRightMouseDown) {
      const delta = event.movementY * -0.01; // Điều chỉnh tốc độ zoom
      const pointer = canvas.getPointer(event.nativeEvent);

      let zoom = canvas.getZoom();
      zoom += delta;
      zoom = Math.min(Math.max(zoom, 0.5), 3); // Giới hạn zoom từ 0.5x đến 3x

      canvas.zoomToPoint(pointer, zoom);
    } else if (isPanning) {
      const deltaX = event.clientX - lastPosRef.current.x;
      const deltaY = event.clientY - lastPosRef.current.y;

      const viewportTransform = canvas.viewportTransform!;
      viewportTransform[4] += deltaX; // Di chuyển ngang
      viewportTransform[5] += deltaY; // Di chuyển dọc

      canvas.setViewportTransform(viewportTransform);
      lastPosRef.current = { x: event.clientX, y: event.clientY };
      // prevent image from being disappeared
      canvas.forEachObject((obj) => obj.setCoords());
      canvas.preserveObjectStacking = true;
      canvas.renderAll();
    }
  };

  const handleWheel = (event) => {
    if (!canvas) return;

    const delta = event.deltaY * -0.001; // Đảo ngược zoom
    let zoom = canvas.getZoom() + delta;
    zoom = Math.min(Math.max(zoom, 0.5), 3); // Giới hạn zoom từ 0.5x đến 3x

    const pointer = canvas.getPointer(event);
    canvas.zoomToPoint(pointer, zoom);
  };

  const handleMouseUp = () => {
    setIsRightMouseDown(false);
    setIsPanning(false);
  };

  const exportImage = async () => {
    if (!canvas || !imageObject) return;

    // const dataURL = canvas.toDataURL({ format: 'png' }); // Lấy ảnh dưới dạng PNG

    const { left = 0, top = 0, width = 0, height = 0, scaleX = 1, scaleY = 1 } = imageObject;
    const realWidth = width * scaleX;
    const realHeight = height * scaleY;

    // Tạo một canvas ẩn để export
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = realWidth;
    offscreenCanvas.height = realHeight;
    const ctx = offscreenCanvas.getContext('2d');

    if (!ctx) return;

    // Xuất ảnh từ Fabric.js vào canvas ẩn
    const tempCanvas = new Canvas(offscreenCanvas);
    const clonedImage = await imageObject.clone();
    clonedImage.set({ left: 0, top: 0 }); // Đặt ảnh vào góc trái canvas ẩn
    tempCanvas.add(clonedImage);

    const objectsInImage = canvas.getObjects().filter((obj) => obj !== imageObject);
    for (const obj of objectsInImage) {
      const clonedObject = await obj.clone();
      clonedObject.set({ left: clonedObject.left! - left, top: clonedObject.top! - top }); // Căn chỉnh vị trí
      tempCanvas.add(clonedObject);
    }

    tempCanvas.renderAll();

    return offscreenCanvas.toDataURL('image/png');
  };

  const hanldeSaveACopy = async () => {
    const dataURL = await exportImage();
    onSaveACopy(
      { src: dataURL, width: imageSize.width, height: imageSize.height },
      { ...imageProps, ...imageSize }
    );
  };

  const handleSave = async () => {
    const dataURL = await exportImage();
    onSave(
      { src: dataURL, width: imageSize.width, height: imageSize.height },
      { ...imageProps, ...imageSize }
    );
  };

  const deleteSelectedObject = () => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.discardActiveObject();
      canvas.renderAll();
    }
  };

  const handleClearAnnotations = () => {
    if (!canvas) return;
    const objectToRemove = canvas.getObjects().filter((obj) => obj !== imageObject);
    for (const obj of objectToRemove) {
      canvas.remove(obj);
    }
    canvas.renderAll();
  };

  return (
    <div>
      <div className="flex">
        <div className="controller mr-2">
          <Form.Item label={t('Width (px)')}>
            <Input
              placeholder={t('Enter Width')}
              type="number"
              value={imageSize.width}
              onChange={handleWidthChange}
            />
          </Form.Item>
          <Form.Item label={t('Height (px)')}>
            <Input
              placeholder={t('Enter Height')}
              type="number"
              value={imageSize.height}
              onChange={handleHeightChange}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={imageSize.manualAdjustment}
              onChange={(e) => handleChangeAdjustment(e.target.checked)}
            >
              {t('Manual edit')}
            </Checkbox>
          </Form.Item>
          <Form.Item label={t('Annotations')}>
            <Row gutter={[8, 8]}>
              {annotations.map((item) => (
                <Col key={item.name}>
                  <Tooltip key={item.name} title={t(item.name)} zIndex={2000}>
                    <Button
                      className="h-10 w-10 bg-background-light-1 p-0"
                      onClick={() => onAddImage(item.src)}
                    >
                      <img src={item.src} style={{ width: 40, height: 40 }} />
                    </Button>
                  </Tooltip>
                </Col>
              ))}
              <Col>
                <Tooltip title={t('Text')} zIndex={2000}>
                  <Button
                    className="flex h-10 w-10 items-center justify-center bg-background-light-1 p-0 text-3xl text-black"
                    onClick={() => onAddText(t('Enter text'))}
                  >
                    <FontSizeOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          </Form.Item>
        </div>
        <div
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
        >
          <canvas ref={canvasRef} className="border border-secondary-main" />
        </div>
      </div>
      <div className="footer-btn mt-4 flex justify-between">
        <div>
          <Button className="mr-2" type="default" onClick={deleteSelectedObject}>
            {t('Remove selected annotation')}
          </Button>
          <Button className="mr-2" type="default" onClick={handleClearAnnotations}>
            {t('Clear all annotations')}
          </Button>
        </div>
        <div>
          <Button className="mr-2" type="default" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button className="mr-2" type="default" onClick={handleSave}>
            {t('Save')}
          </Button>
          <Button type="primary" onClick={hanldeSaveACopy}>
            {t('Save as copy')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MedicalImageEdit;
